import { TaxTreatmentEnum } from '@pxtra-client-lib-legacy/gen';
import { useTranslation } from 'react-i18next';
import { InputFieldLabel } from '../../../../lib/components/atomic/input/shared/fieldLabel';
import { CurrencyField } from '../../../../lib/components/atomic/input/currencyField';
import { CheckboxGroup } from '../../../../lib/components/atomic/input/checkboxGroup';
import Util from '../../../../misc/util';
import { TAX_TREATMENT_MAX_AMOUNT } from '../utils/validationSchemas';
import { useFormikContext, FormikValues } from 'formik';
import { useCreateBonus } from '../hooks/useCreateBonus';

interface CurrencyFrequencyFieldProps {
  hideFrequency?: boolean;
}

export const CurrencyFrequencyField = ({ hideFrequency = false }: CurrencyFrequencyFieldProps) => {
  const { t } = useTranslation();
  const { newBonus } = useCreateBonus();
  const { values, touched, errors, setFieldValue, setValues, setTouched } =
    useFormikContext<FormikValues>();

  const checkboxOptions = [
    { value: 'ONE_TIME', label: t('bonus:oneTime') },
    { value: 'RECURRING', label: t('bonus:recurring') }
  ];

  return (
    <div className="w-full">
      <InputFieldLabel
        label={`${t('common:AMOUNT')} (€)`}
        required
        tooltip={values.payoutType === 'APP_VOUCHER' ? t('bonus:tooltip:amount') : undefined}
      />
      <CurrencyField
        value={values.amount}
        placeholder={
          values.taxTreatment === 'FULLY_TAXED'
            ? t('common:exampleCurrency')
            : t('common:maxCurrency', {
                amount: Util.formatCurrency(
                  (values.taxTreatment &&
                    TAX_TREATMENT_MAX_AMOUNT[values.taxTreatment as TaxTreatmentEnum]) ??
                    10000
                )
              })
        }
        helperText={(touched.amount && errors.amount) as string}
        name="amount"
        error={touched.amount && !!errors.amount}
        disabled={newBonus.editingNotAllowed}
        onChange={(e) => {
          setFieldValue('amount', Number(e.target.value));
        }}
      />
      {!hideFrequency && (
        <CheckboxGroup
          options={checkboxOptions}
          value={values.frequency ?? undefined}
          error={touched.frequency && !!errors.frequency}
          helperText={(touched.frequency && errors.frequency) as string}
          disabled={newBonus.editingNotAllowed}
          tooltip={newBonus.editingNotAllowed ? t('bonus:tooltip:editingNotAllowed') : undefined}
          onChange={(value) => {
            const isOneTime = value === 'ONE_TIME';
            const isSpecialOccasion = ['ANNIVERSARY', 'BIRTHDAY'].includes(
              values.specialOccasionType
            );

            setTouched({
              ...touched,
              frequency: true,
              salaryType: false,
              salaryTypeId: false,
              ...(isOneTime && {
                ...(!isSpecialOccasion && { startAccountingPeriod: false }),
                endAccountingPeriod: false,
                anniversaryInterval: false,
                interval: false
              }),
              ...(!isOneTime && {
                payoutMonth: false
              })
            });

            setValues({
              ...values,
              frequency: value,
              ...(isOneTime && {
                ...(!isSpecialOccasion && { startAccountingPeriod: undefined }),
                endAccountingPeriod: undefined,
                anniversaryInterval: undefined,
                interval: undefined
              }),
              ...(!isOneTime && {
                payoutMonth: undefined
              })
            });
          }}
        />
      )}
    </div>
  );
};
